import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import EduLogo from "../components/EduLogo";
import { useAuth } from "../context/AuthContext";
import { defaultRouteFor } from "../components/ProtectedRoute";
import { api } from "../lib/api";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [showForgot, setShowForgot] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    useEffect(() => {
        if (user && user !== false) navigate(defaultRouteFor(user.role));
    }, [user, navigate]);

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setErr("");
        try {
            const u = await login(email, password);
            navigate(defaultRouteFor(u.role));
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setLoading(false);
        }
    }

    async function handleForgot() {
        if (!forgotEmail) return;
        setForgotLoading(true);
        try {
            await api.post("/auth/forgot-password", { email: forgotEmail });
            setForgotSent(true);
        } catch {
            setForgotSent(true); // igual mostramos éxito para no revelar si el email existe
        } finally {
            setForgotLoading(false);
        }
    }

    return (
        <div className="min-h-screen bg-edu-bg flex items-center justify-center p-4 relative overflow-hidden">
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-edu-gold/10 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-edu-navy/10 blur-3xl" />
            </div>

            <div className="relative w-full max-w-md fade-in">
                <div className="flex justify-center mb-8">
                    <EduLogo size="lg" />
                </div>

                <div className="edu-card p-8 md:p-10">
                    <h1 className="text-3xl font-semibold tracking-tight text-[#0A0A0A] text-center mb-2" data-testid="login-title">
                        Bienvenido de vuelta
                    </h1>
                    <p className="text-center text-sm text-edu-body mb-7">
                        Ingresa con tu cuenta para continuar tu aprendizaje
                    </p>

                    {!showForgot ? (
                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-widest text-edu-muted mb-1.5 block">
                                    Correo Electrónico
                                </label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="tu@correo.com"
                                    className="edu-input w-full"
                                    data-testid="login-email-input"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-widest text-edu-muted mb-1.5 block">
                                    Contraseña
                                </label>
                                <div className="relative">
                                    <input
                                        type={showPwd ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="edu-input w-full pr-10"
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd((s) => !s)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-edu-muted"
                                        data-testid="login-toggle-password"
                                    >
                                        {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {err && (
                                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                                    {err}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="edu-btn-primary w-full py-3 font-semibold mt-2 disabled:opacity-60"
                                data-testid="login-submit-button"
                            >
                                {loading ? "Iniciando…" : "Iniciar Sesión"}
                            </button>

                            <button
                                type="button"
                                onClick={() => { setShowForgot(true); setForgotSent(false); setForgotEmail(""); }}
                                className="block w-full text-center text-sm text-edu-navy hover:underline"
                                data-testid="login-forgot-link"
                            >
                                ¿Olvidaste tu contraseña?
                            </button>
                        </form>
                    ) : forgotSent ? (
                        <div className="space-y-4 fade-in text-center">
                            <div className="text-4xl">📧</div>
                            <h3 className="text-lg font-semibold">Revisa tu correo</h3>
                            <p className="text-sm text-edu-body">
                                Si el email existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña en los próximos minutos.
                            </p>
                            <button
                                onClick={() => { setShowForgot(false); setForgotSent(false); }}
                                className="block w-full text-sm text-edu-navy text-center hover:underline"
                            >
                                Volver al inicio de sesión
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4 fade-in">
                            <p className="text-sm text-edu-body">
                                Ingresa tu correo y enviaremos las instrucciones para recuperar tu contraseña.
                            </p>
                            <input
                                type="email"
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                                placeholder="tu@correo.com"
                                className="edu-input w-full"
                                data-testid="forgot-email-input"
                            />
                            <button
                                onClick={handleForgot}
                                disabled={forgotLoading || !forgotEmail}
                                className="edu-btn-primary w-full py-2.5 disabled:opacity-60"
                                data-testid="forgot-submit-button"
                            >
                                {forgotLoading ? "Enviando…" : "Enviar instrucciones"}
                            </button>
                            <button
                                onClick={() => setShowForgot(false)}
                                className="block w-full text-sm text-edu-navy text-center hover:underline"
                                data-testid="forgot-back-link"
                            >
                                Volver al inicio de sesión
                            </button>
                        </div>
                    )}

                    <div className="mt-8 pt-5 border-t border-edu">
                        <div className="text-[10px] text-edu-muted text-center">
                            Soporte: soporte@grit.pe
                        </div>
                    </div>
                </div>

                <p className="text-center text-xs text-edu-muted mt-6">EduPeru v1.0.0 · Aprendizaje Personalizado</p>
            </div>
        </div>
    );
}
